import React, { useEffect, useRef } from 'react';

interface BubbleBackgroundProps {
  cardRef: React.RefObject<HTMLDivElement>;
}

const LETTERS = ['S', 'O'];
const LETTER_COLORS = [
  '#7DD3FC', // sky blue
  '#C4B5FD', // pastel mor
  '#6EE7B7', // mint
  '#FDE68A', // soft sarı
  '#FBCFE8', // pembe
  '#A78BFA', // mor
  '#38BDF8', // sky blue
  '#34D399', // emerald
  '#F472B6', // pembe
  '#FDBA74', // soft turuncu
];
const LETTER_COUNT = 18;

function randomBetween(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

const LetterBackground: React.FC<BubbleBackgroundProps> = ({ cardRef }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const lettersRef = useRef<any[]>([]);

  useEffect(() => {
    if (window.innerWidth < 768) return; // Sadece masaüstü
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    function handleResize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    }
    window.addEventListener('resize', handleResize);

    function getCardRect() {
      if (!cardRef.current) return null;
      return cardRef.current.getBoundingClientRect();
    }

    function createLetter() {
      let cardRect = getCardRect();
      let x, y;
      if (cardRect) {
        const side = Math.floor(Math.random() * 4);
        if (side === 0) {
          x = randomBetween(0, cardRect.left - 40);
          y = randomBetween(0, height);
        } else if (side === 1) {
          x = randomBetween(cardRect.right + 40, width);
          y = randomBetween(0, height);
        } else if (side === 2) {
          x = randomBetween(0, width);
          y = randomBetween(0, cardRect.top - 40);
        } else {
          x = randomBetween(0, width);
          y = randomBetween(cardRect.bottom + 40, height);
        }
      } else {
        x = randomBetween(0, width);
        y = randomBetween(0, height);
      }
      const letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
      const color = LETTER_COLORS[Math.floor(Math.random() * LETTER_COLORS.length)];
      const fontSize = randomBetween(54, 160);
      return {
        x,
        y,
        letter,
        color,
        fontSize,
        speed: randomBetween(0.07, 0.16),
        dx: randomBetween(-0.07, 0.07),
        pulseSpeed: randomBetween(0.7, 1.3),
        pulsePhase: Math.random() * Math.PI * 2,
        rotation: randomBetween(-Math.PI/6, Math.PI/6),
        rotationSpeed: randomBetween(-0.001, 0.001),
        shadow: Math.random() > 0.5,
      };
    }

    // İlk harfleri oluştur
    lettersRef.current = Array.from({ length: LETTER_COUNT }).map(createLetter);

    function isInsideCard(x: number, y: number, fontSize: number, cardRect: DOMRect | null) {
      if (!cardRect) return false;
      const r = fontSize * 0.6;
      return (
        x + r > cardRect.left &&
        x - r < cardRect.right &&
        y + r > cardRect.top &&
        y - r < cardRect.bottom
      );
    }

    function animate(ts?: number) {
      ctx.clearRect(0, 0, width, height);
      const cardRect = getCardRect();
      for (const l of lettersRef.current) {
        // Pulse (nefes) efekti
        const pulse = 0.35 + 0.25 * Math.abs(Math.sin((ts || 0) * 0.001 * l.pulseSpeed + l.pulsePhase));
        ctx.save();
        ctx.globalAlpha = pulse;
        ctx.font = `700 ${l.fontSize}px 'Quicksand', 'Montserrat', 'Arial', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.translate(l.x, l.y);
        ctx.rotate(l.rotation);
        // Kart kutusunun üstüne geliyorsa çizmiyoruz
        if (!isInsideCard(l.x, l.y, l.fontSize, cardRect)) {
          // Glow/shadow efekti
          if (l.shadow) {
            ctx.shadowColor = l.color + '99';
            ctx.shadowBlur = l.fontSize * 0.18;
          }
          // Hafif beyaz highlight
          ctx.save();
          ctx.globalAlpha = pulse * 0.18;
          ctx.shadowColor = '#fff';
          ctx.shadowBlur = l.fontSize * 0.12;
          ctx.fillStyle = '#fff';
          ctx.fillText(l.letter, 0, 0);
          ctx.restore();
          // Ana harf
          ctx.fillStyle = l.color + 'cc';
          ctx.globalAlpha = pulse;
          ctx.fillText(l.letter, 0, 0);
        }
        ctx.restore();
        // Hareket ve animasyon
        l.y -= l.speed;
        l.x += l.dx;
        l.rotation += l.rotationSpeed;
        if (l.y + l.fontSize < 0 || l.y - l.fontSize > height || l.x + l.fontSize < 0 || l.x - l.fontSize > width) {
          Object.assign(l, createLetter());
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [cardRef]);

  if (typeof window !== 'undefined' && window.innerWidth < 768) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 10,
        pointerEvents: 'none',
        opacity: 0.95,
        transition: 'opacity 0.5s',
        userSelect: 'none',
      }}
      aria-hidden="true"
    />
  );
};

export default LetterBackground; 